/* ════════════════════════════════════════════════════════════════
   MOAVI MOON — Archive Theme · theme.js
   Vanilla JS, sin dependencias. Custom Elements según las buenas
   prácticas de Shopify. Cada componente es independiente.
   ════════════════════════════════════════════════════════════════ */

const THEME = JSON.parse(document.getElementById('theme-data')?.textContent || '{}');

const formatMoney = (cents) => {
  const format = THEME.moneyFormat || '€{{amount}}';
  const amount = (cents / 100).toFixed(2).replace('.', ',');
  return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
};

const trapFocus = (container) => {
  const focusables = container.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])');
  if (!focusables.length) return () => {};
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const handler = (e) => {
    if (e.key !== 'Tab') return;
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  container.addEventListener('keydown', handler);
  return () => container.removeEventListener('keydown', handler);
};

/* ── Reveal on scroll ─────────────────────────────────────────── */
const revealObserver = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      entry.target.classList.add('is-revealed');
      revealObserver.unobserve(entry.target);
    }
  });
}, { rootMargin: '-60px 0px' });

const initReveals = (root = document) => {
  root.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el, i) => {
    el.style.setProperty('--reveal-delay', `${(el.dataset.revealDelay ?? Math.min(i % 6, 4) * 0.07)}s`);
    revealObserver.observe(el);
  });
};
initReveals();
document.addEventListener('shopify:section:load', (e) => initReveals(e.target));

/* ── Header: scroll y ocultación ──────────────────────────────── */
class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.lastY = 0;
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    // La barra de anuncios es opcional (el usuario la puede ocultar desde el
    // editor) y su altura es configurable — nunca asumir un valor fijo acá.
    // Se mide el DOM real en vez de adivinar, para que el header siempre
    // quede pegado justo donde termina lo que sea que esté arriba (o en 0
    // si no hay nada), sin importar cómo esté configurada esa sección.
    this.updateAnnounceOffset = this.updateAnnounceOffset.bind(this);
    this.updateAnnounceOffset();
    window.addEventListener('resize', this.updateAnnounceOffset, { passive: true });
    this.onScroll();
  }
  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.updateAnnounceOffset);
  }
  updateAnnounceOffset() {
    const bar = document.querySelector('.announce-bar');
    const height = bar ? bar.offsetHeight : 0;
    document.documentElement.style.setProperty('--announce-offset', `${height}px`);
  }
  onScroll() {
    const y = window.scrollY;
    this.classList.toggle('is-scrolled', y > 24);
    if (this.dataset.hideOnScroll === 'true') {
      this.classList.toggle('is-hidden', y > 140 && y > this.lastY && !document.body.classList.contains('overlay-open'));
    }
    this.lastY = y;
  }
}
customElements.define('site-header', SiteHeader);

/* ── Overlays (drawers y modales) ─────────────────────────────── */
class OverlayEl extends HTMLElement {
  connectedCallback() {
    this.releaseFocus = null;
    // Delegación en el propio elemento: sobrevive al reemplazo de innerHTML
    // que hace la Section Rendering API al actualizar el carrito.
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-overlay-close]') || e.target.classList.contains('scrim')) {
        this.close();
      }
    });
    this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
  }
  open() {
    this.classList.add('is-open');
    document.body.classList.add('overlay-open');
    this.releaseFocus = trapFocus(this);
    const focusTarget = this.querySelector('[data-autofocus]') || this.querySelector('button, a, input');
    setTimeout(() => focusTarget?.focus(), 120);
    this.dispatchEvent(new CustomEvent('overlay:open'));
  }
  close() {
    this.classList.remove('is-open');
    if (!document.querySelector('overlay-el.is-open')) document.body.classList.remove('overlay-open');
    this.releaseFocus?.();
  }
}
customElements.define('overlay-el', OverlayEl);

document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-overlay-open]');
  if (!trigger) return;
  const overlay = document.getElementById(trigger.dataset.overlayOpen);
  if (overlay) { e.preventDefault(); overlay.open(); }
});

/* ── Menú: desplegar submenús (SHOP → tipos de prenda) ────────── */
document.addEventListener('click', (e) => {
  const item = e.target.closest('.menu-drawer__item');
  if (!item) return;
  const group = item.closest('.menu-drawer__group');
  const sub = group && group.querySelector('.menu-drawer__sub');
  if (!sub) return; // sin subenlaces: el enlace navega con normalidad
  e.preventDefault();
  const open = group.classList.toggle('is-expanded');
  item.setAttribute('aria-expanded', open ? 'true' : 'false');
});

/* ── Carrito AJAX ─────────────────────────────────────────────── */
class CartAPI {
  static async request(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw await res.json().catch(() => ({ description: THEME.strings?.cartError }));
    return res.json();
  }
  static add(items) {
    return this.request(THEME.routes.cartAdd, { items, sections: 'cart-drawer,cart-icon-bubble' });
  }
  static change(line, quantity) {
    return this.request(THEME.routes.cartChange, { line, quantity, sections: 'cart-drawer,cart-icon-bubble' });
  }
  static refreshSections(sections) {
    if (!sections) return;
    if (sections['cart-drawer']) {
      const current = document.getElementById('CartDrawer');
      if (current) {
        const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const next = html.getElementById('CartDrawer');
        if (next) {
          const wasOpen = current.classList.contains('is-open');
          current.innerHTML = next.innerHTML;
          if (wasOpen) current.classList.add('is-open');
        }
      }
    }
    if (sections['cart-icon-bubble']) {
      document.querySelectorAll('[data-cart-icon-bubble]').forEach((el) => {
        el.innerHTML = new DOMParser()
          .parseFromString(sections['cart-icon-bubble'], 'text/html')
          .querySelector('[data-cart-icon-bubble]')?.innerHTML ?? el.innerHTML;
      });
    }
  }
}

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.submitBtn = this.querySelector('[type="submit"]');
    this.form?.addEventListener('submit', (e) => this.onSubmit(e));
  }
  async onSubmit(e) {
    if (THEME.cartType !== 'drawer') return;
    e.preventDefault();
    const idInput = this.form.querySelector('[name="id"]');
    if (!idInput?.value) return;
    const original = this.submitBtn.textContent;
    this.submitBtn.setAttribute('disabled', '');
    this.submitBtn.textContent = THEME.strings.adding;
    try {
      const qty = Number(this.form.querySelector('[name="quantity"]')?.value || 1);
      const props = {};
      this.form.querySelectorAll('[name^="properties["]').forEach((el) => {
        const key = el.name.slice(11, -1);
        if (el.value) props[key] = el.value;
      });
      const data = await CartAPI.add([{ id: Number(idInput.value), quantity: qty, properties: props }]);
      CartAPI.refreshSections(data.sections);
      document.getElementById('CartDrawer')?.open();
    } catch (err) {
      this.publishError(err.description || THEME.strings.cartError);
    } finally {
      this.submitBtn.removeAttribute('disabled');
      this.submitBtn.textContent = original;
    }
  }
  publishError(message) {
    let el = this.querySelector('.form-message');
    if (!el) {
      el = document.createElement('p');
      el.className = 'form-message form-message--error';
      el.setAttribute('role', 'alert');
      this.form.append(el);
    }
    el.textContent = message;
  }
}
customElements.define('product-form', ProductForm);

class CartLine extends HTMLElement {
  connectedCallback() {
    this.line = Number(this.dataset.line);
    this.querySelectorAll('[data-qty-change]').forEach((btn) =>
      btn.addEventListener('click', () => this.change(Number(this.querySelector('input').value) + Number(btn.dataset.qtyChange))));
    this.querySelector('[data-line-remove]')?.addEventListener('click', () => this.change(0));
    this.querySelector('input')?.addEventListener('change', (e) => this.change(Number(e.target.value)));
  }
  async change(quantity) {
    this.style.opacity = '.4';
    try {
      const data = await CartAPI.change(this.line, Math.max(0, quantity));
      CartAPI.refreshSections(data.sections);
    } catch { this.style.opacity = '1'; }
  }
}
customElements.define('cart-line', CartLine);

/* ── Complete the Fit — recomendaciones nativas + fallback ────── */
class CartRecommendations extends HTMLElement {
  connectedCallback() {
    const { url, productId, sectionId } = this.dataset;
    if (!url || !productId) return;
    const limit = this.dataset.limit || 2;
    const endpoint = `${url}?section_id=${sectionId}&product_id=${productId}&limit=${limit}&intent=related`;
    fetch(endpoint)
      .then((r) => (r.ok ? r.text() : Promise.reject()))
      .then((html) => {
        const recs = new DOMParser().parseFromString(html, 'text/html').querySelector('.cart-recs');
        // Solo reemplazar el fallback si Shopify devolvió al menos una tarjeta.
        if (recs && recs.querySelector('.cart-rec-card')) {
          this.innerHTML = '';
          this.appendChild(recs);
        }
      })
      .catch(() => {});
  }
}
customElements.define('cart-recommendations', CartRecommendations);

/* Quick-add (Complete the Fit): añade sin cerrar el drawer ni recargar */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-quick-add]');
  if (!btn) return;
  e.preventDefault();
  const id = Number(btn.dataset.quickAdd);
  if (!id || btn.hasAttribute('disabled')) return;
  const original = btn.textContent;
  btn.setAttribute('disabled', '');
  btn.textContent = THEME.strings.adding;
  try {
    const data = await CartAPI.add([{ id, quantity: 1 }]);
    CartAPI.refreshSections(data.sections);
    document.getElementById('CartDrawer')?.open();
  } catch {
    btn.removeAttribute('disabled');
    btn.textContent = original;
  }
});

/* ── Variantes de producto ────────────────────────────────────── */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.addEventListener('change', () => this.onChange());
    this.updateAvailability();
  }
  get selectedOptions() {
    return this.fieldsets.map((fs) => fs.querySelector('input:checked')?.value);
  }
  // Marca cada talla como agotada (línea diagonal) según las otras opciones elegidas.
  updateAvailability() {
    const selected = this.selectedOptions;
    this.fieldsets.forEach((fs, optIndex) => {
      fs.querySelectorAll('.option-value').forEach((label) => {
        const input = label.querySelector('input');
        if (!input) return;
        const available = this.variants.some(
          (v) =>
            v.options[optIndex] === input.value &&
            v.available &&
            v.options.every((o, i) => i === optIndex || selected[i] == null || o === selected[i])
        );
        label.classList.toggle('is-unavailable', !available);
        if (!input.checked) input.disabled = !available;
      });
    });
  }
  onChange() {
    this.updateAvailability();
    const options = this.selectedOptions;
    const variant = this.variants.find((v) => v.options.every((o, i) => o === options[i]));
    const form = document.getElementById(this.dataset.formId);
    const idInput = form?.querySelector('[name="id"]');
    const btn = form?.querySelector('[type="submit"]');
    const priceEl = document.querySelector(`[data-price-wrapper="${this.dataset.section}"]`);

    if (variant) {
      idInput.value = variant.id;
      if (window.history.replaceState && this.dataset.updateUrl === 'true') {
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
      }
      if (priceEl) {
        priceEl.innerHTML = variant.compare_at_price > variant.price
          ? `<s class="price--compare">${formatMoney(variant.compare_at_price)}</s>${formatMoney(variant.price)}`
          : formatMoney(variant.price);
      }
      if (btn) {
        btn.disabled = !variant.available;
        btn.textContent = variant.available ? THEME.strings.addToCart : THEME.strings.soldOut;
      }
      document.querySelectorAll(`[data-sticky-variant="${this.dataset.section}"]`)
        .forEach((el) => { el.textContent = variant.title; });
    } else if (btn) {
      btn.disabled = true;
      btn.textContent = THEME.strings.unavailable;
    }
  }
}
customElements.define('variant-picker', VariantPicker);

/* ── Galería PDP ──────────────────────────────────────────────── */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('.product-gallery__stage');
    this.slides = Array.from(this.querySelectorAll('.product-gallery__slide'));
    this.thumbs = Array.from(this.querySelectorAll('.product-gallery__thumb'));
    this.dots = Array.from(this.querySelectorAll('.product-gallery__dot'));

    this.thumbs.forEach((thumb) =>
      thumb.addEventListener('click', () => this.select(Number(thumb.dataset.thumb) || 0)));

    // Puntos de posición del carrusel en móvil. Sin requestAnimationFrame:
    // no se ejecuta si la pestaña no se está dibujando y los puntos se
    // quedarían congelados. Alternar clases en 4–10 nodos es barato.
    if (this.stage && this.dots.length) {
      this.stage.addEventListener('scroll', () => {
        const i = Math.round(this.stage.scrollLeft / this.stage.clientWidth);
        this.dots.forEach((dot, n) => dot.classList.toggle('is-active', n === i));
      }, { passive: true });
    }

    // El zoom abre la imagen que se está viendo: en escritorio la activa,
    // en móvil la que quedó centrada tras deslizar. El panel solo se puede
    // desplazar cuando el modal ya es visible, de ahí el pequeño retardo.
    this.querySelector('.product-gallery__zoom')?.addEventListener('click', () => {
      const index = this.currentIndex();
      setTimeout(() => {
        const panel = document.querySelector('#ZoomModal .modal__panel');
        const item = document.querySelector(`#ZoomModal [data-zoom-slide="${index}"]`);
        if (panel && item) panel.scrollTop = Math.max(0, item.offsetTop - 48);
      }, 80);
    });
  }

  /** Índice visible: en móvil manda el scroll del carrusel. */
  currentIndex() {
    if (this.stage && this.stage.scrollWidth > this.stage.clientWidth + 1) {
      return Math.round(this.stage.scrollLeft / this.stage.clientWidth);
    }
    return Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
  }

  select(index) {
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
    this.thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
  }
}
customElements.define('product-gallery', ProductGallery);

/* ── Carrusel de imágenes en la tarjeta de producto ───────────── */
class CardSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-pc-track]');
    if (!this.track) return;
    this.dots = Array.from(this.querySelectorAll('.pc-slider__dot'));
    this.prev = this.querySelector('[data-pc-prev]');
    this.next = this.querySelector('[data-pc-next]');
    this.count = this.dots.length || 1;

    this.prev?.addEventListener('click', (e) => { e.preventDefault(); this.go(-1); });
    this.next?.addEventListener('click', (e) => { e.preventDefault(); this.go(1); });

    let raf;
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => this.sync());
    }, { passive: true });
  }
  get index() {
    const w = this.track.clientWidth || 1;
    return Math.round(this.track.scrollLeft / w);
  }
  go(step) {
    const w = this.track.clientWidth;
    const target = Math.min(Math.max(this.index + step, 0), this.count - 1);
    this.track.scrollTo({ left: target * w, behavior: 'smooth' });
  }
  sync() {
    const i = this.index;
    this.dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
    if (this.prev) this.prev.hidden = i <= 0;
    if (this.next) this.next.hidden = i >= this.count - 1;
  }
}
customElements.define('card-slider', CardSlider);

/* ── Size Guide: toggle Centimetres / Inches ──────────────────── */
class SizeUnits extends HTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('[data-unit-btn]'));
    this.panels = Array.from(this.querySelectorAll('[data-unit-panel]'));
    this.buttons.forEach((btn) =>
      btn.addEventListener('click', () => this.show(btn.dataset.unitBtn))
    );
  }
  show(unit) {
    this.buttons.forEach((b) => b.classList.toggle('is-active', b.dataset.unitBtn === unit));
    this.panels.forEach((p) => { p.hidden = p.dataset.unitPanel !== unit; });
  }
}
customElements.define('size-units', SizeUnits);

/* ── Sticky Add To Cart ───────────────────────────────────────── */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    const target = document.getElementById(this.dataset.watch);
    if (!target) return;
    new IntersectionObserver(([entry]) => {
      this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
    }).observe(target);
    this.querySelector('[data-sticky-submit]')?.addEventListener('click', () => {
      document.getElementById(this.dataset.form)?.querySelector('[type="submit"]')?.click();
    });
  }
}
customElements.define('sticky-atc', StickyAtc);

/* ── Búsqueda predictiva ──────────────────────────────────────── */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-search-results]');
    this.idle = this.querySelector('[data-search-idle]');
    if (!this.input || THEME.predictiveSearch === false) return;
    let timer;
    this.input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => this.search(this.input.value.trim()), 200);
    });
  }
  async search(query) {
    if (query.length < 2) {
      this.results.hidden = true;
      if (this.idle) this.idle.hidden = false;
      return;
    }
    try {
      const url = `${THEME.routes.predictiveSearch}?q=${encodeURIComponent(query)}&resources[type]=product,collection,article,page&resources[limit]=8&section_id=predictive-search`;
      const res = await fetch(url);
      const text = await res.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      this.results.innerHTML = html.querySelector('#predictive-search-results')?.innerHTML ?? '';
      this.results.hidden = false;
      if (this.idle) this.idle.hidden = true;
    } catch { /* red caída: mantener estado */ }
  }
}
customElements.define('predictive-search', PredictiveSearch);

/* ── Wishlist (localStorage, escopada por Customer Account) ───── */
/*
  Integración con Shopify Customer Accounts:
  — Invitado: la lista vive en la clave anónima.
  — Cliente con sesión: la lista vive en una clave propia por customer.id,
    y al detectar el inicio de sesión la lista anónima se FUSIONA (unión)
    en la del cliente una sola vez. Así los favoritos "vuelven" cuando el
    cliente inicia sesión de nuevo en este navegador.
  Limitación honesta: la sincronización entre dispositivos requiere una
  app o la Customer Account API (fuera del alcance de un theme).
*/
const WISHLIST_BASE = 'moavimoon:wishlist';
const Wishlist = {
  key() {
    return THEME.customerId ? `${WISHLIST_BASE}:c${THEME.customerId}` : WISHLIST_BASE;
  },
  read(key) { try { return JSON.parse(localStorage.getItem(key)) || []; } catch { return []; } },
  get() { return this.read(this.key()); },
  save(list) { try { localStorage.setItem(this.key(), JSON.stringify(list)); } catch { /* sin almacenamiento */ } },
  mergeGuestIntoCustomer() {
    if (!THEME.customerId) return;
    const guest = this.read(WISHLIST_BASE);
    if (!guest.length) return;
    const merged = [...new Set([...this.get(), ...guest])];
    this.save(merged);
    try { localStorage.removeItem(WISHLIST_BASE); } catch { /* sin almacenamiento */ }
  },
  toggle(handle) {
    const list = this.get();
    const next = list.includes(handle) ? list.filter((h) => h !== handle) : [...list, handle];
    this.save(next);
    this.paint();
    return next.includes(handle);
  },
  paint(root = document) {
    const list = this.get();
    root.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const active = list.includes(btn.dataset.wishlistToggle);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active);
    });
  },
};
Wishlist.mergeGuestIntoCustomer();
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-wishlist-toggle]');
  if (!btn) return;
  e.preventDefault();
  Wishlist.toggle(btn.dataset.wishlistToggle);
});
Wishlist.paint();
document.addEventListener('shopify:section:load', (e) => Wishlist.paint(e.target));

/* ── Wishlist / Recently viewed: render de tarjetas ───────────── */
class ProductFetchGrid extends HTMLElement {
  async connectedCallback() {
    let handles = [];
    if (this.dataset.source === 'wishlist') handles = Wishlist.get();
    if (this.dataset.source === 'recent') {
      try { handles = JSON.parse(localStorage.getItem('moavimoon:recent')) || []; } catch { handles = []; }
      handles = handles.filter((h) => h !== this.dataset.exclude);
    }
    handles = handles.slice(0, Number(this.dataset.limit || 4));
    if (!handles.length) { this.closest('[data-fetch-section]')?.remove(); return; }
    const cards = await Promise.all(handles.map(async (handle) => {
      try {
        const res = await fetch(`/products/${handle}?section_id=product-card-standalone`);
        return res.ok ? res.text() : '';
      } catch { return ''; }
    }));
    this.innerHTML = cards.join('');
    this.querySelectorAll('.shopify-section').forEach((el) => { el.style.display = 'contents'; });
    Wishlist.paint(this);
    initReveals(this);
  }
}
customElements.define('product-fetch-grid', ProductFetchGrid);

if (document.body.dataset.productHandle) {
  try {
    const list = JSON.parse(localStorage.getItem('moavimoon:recent')) || [];
    const next = [document.body.dataset.productHandle, ...list.filter((h) => h !== document.body.dataset.productHandle)].slice(0, 8);
    localStorage.setItem('moavimoon:recent', JSON.stringify(next));
  } catch { /* sin almacenamiento */ }
}

/* ── Countdown ────────────────────────────────────────────────── */
class CountdownTimer extends HTMLElement {
  connectedCallback() {
    const target = new Date(this.dataset.date).getTime();
    if (Number.isNaN(target)) return;
    const units = ['days', 'hours', 'minutes', 'seconds'];
    const tick = () => {
      const diff = Math.max(0, target - Date.now());
      const values = [
        Math.floor(diff / 86400000),
        Math.floor((diff % 86400000) / 3600000),
        Math.floor((diff % 3600000) / 60000),
        Math.floor((diff % 60000) / 1000),
      ];
      units.forEach((unit, i) => {
        const el = this.querySelector(`[data-unit="${unit}"]`);
        if (el) el.textContent = String(values[i]).padStart(2, '0');
      });
    };
    tick();
    this.interval = setInterval(tick, 1000);
  }
  disconnectedCallback() { clearInterval(this.interval); }
}
customElements.define('countdown-timer', CountdownTimer);

/* ── Password page: revelar "Enter using password" ────────────── */
document.addEventListener('click', (e) => {
  const toggle = e.target.closest('[data-pw-toggle]');
  if (!toggle) return;
  const target = document.getElementById(toggle.getAttribute('aria-controls'));
  if (!target) return;
  target.hidden = false;
  toggle.setAttribute('aria-expanded', 'true');
  toggle.hidden = true;
  target.querySelector('input')?.focus();
});

/* ── Claves compartidas de la secuencia de bienvenida ─────────── */
const LOC_CONFIRMED = 'moavimoon:location-confirmed';
const LOC_JUST = 'moavimoon:location-just-confirmed';

/* ── Archive Gate (Join List) ─────────────────────────────────── */
class ArchiveGate extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('overlay-el');
    if (!this.overlay) return;
    this.bindHandlers();
    if (this.dataset.enabled !== 'true') return;
    if (window.Shopify && window.Shopify.designMode) return; // en el editor solo se abre manualmente

    // Coordinación con el modal de ubicación: si aún no se ha confirmado la
    // ubicación o se acaba de confirmar, el gate NO se auto-abre — lo abrirá
    // el LocationModal en la secuencia correcta (país → 2s → Join List).
    const locModal = document.getElementById('LocationModal');
    const locationPending = locModal && localStorage.getItem(LOC_CONFIRMED) !== 'true';
    const justConfirmed = sessionStorage.getItem(LOC_JUST) === 'true';

    this.bindGateLinks();
    if (locationPending || justConfirmed) return;

    if (this.shouldOpen()) {
      setTimeout(() => this.overlay.open(), Number(this.dataset.delay || 1) * 1000);
    }
  }
  shouldOpen() {
    const frequency = this.dataset.frequency;
    const joined = localStorage.getItem('moavimoon:archive-member') === 'joined';
    const dismissedAt = Number(localStorage.getItem('moavimoon:gate-dismissed-at') || 0);
    const freshDismiss = frequency === 'session'
      ? sessionStorage.getItem('moavimoon:gate-dismissed') === 'true'
      : Date.now() - dismissedAt < Number(frequency) * 86400000;
    return !joined && !freshDismiss;
  }
  maybeOpen() { if (this.dataset.enabled === 'true' && this.shouldOpen()) this.overlay.open(); }
  bindGateLinks() {
    if (this.dataset.gateLinks !== 'true' || !this.shouldOpen()) return;
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href*="/products/"], a[href*="/collections/"]');
      if (!link || this.gateShown || link.closest('.site-header')) return;
      e.preventDefault();
      this.pendingHref = link.href;
      this.gateShown = true;
      this.overlay.open();
    }, true);
  }
  bindHandlers() {
    this.querySelector('[data-gate-dismiss]')?.addEventListener('click', () => { this.dismiss(); this.proceed(); });
    this.querySelector('form')?.addEventListener('submit', () => {
      localStorage.setItem('moavimoon:archive-member', 'joined');
    });
  }
  dismiss() {
    sessionStorage.setItem('moavimoon:gate-dismissed', 'true');
    localStorage.setItem('moavimoon:gate-dismissed-at', String(Date.now()));
    this.overlay.close();
  }
  proceed() { if (this.pendingHref) window.location.href = this.pendingHref; }
}
customElements.define('archive-gate', ArchiveGate);

/* ── Location Modal (Shopify Markets: país / idioma / moneda) ──── */
class LocationModal extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('overlay-el');
    if (!this.overlay) return;
    const form = this.querySelector('form');

    // Al confirmar: guardar preferencia y dejar que el form de localización se
    // envíe (recarga que aplica el mercado/idioma/moneda de Shopify).
    form?.addEventListener('submit', () => {
      localStorage.setItem(LOC_CONFIRMED, 'true');
      sessionStorage.setItem(LOC_JUST, 'true');
    });

    if (window.Shopify && window.Shopify.designMode) return;

    const confirmed = localStorage.getItem(LOC_CONFIRMED) === 'true';
    const justConfirmed = sessionStorage.getItem(LOC_JUST) === 'true';

    if (justConfirmed) {
      // Acabamos de confirmar (la página recargó): no reabrir el modal;
      // tras la espera, mostrar el Join List.
      sessionStorage.removeItem(LOC_JUST);
      const delay = Number(this.dataset.delay || 2) * 1000;
      setTimeout(() => document.querySelector('archive-gate')?.maybeOpen?.(), delay);
      return;
    }

    // Primera visita: cookies primero, luego este modal.
    if (this.dataset.enabled === 'true' && this.dataset.hasOptions === 'true' && !confirmed) {
      this.whenCookiesSettled(() => setTimeout(() => this.overlay.open(), 400));
    }
  }

  /* Espera a que el visitante responda el banner de cookies de Shopify.
     Si no hay banner (o el ajuste está desactivado), continúa sin esperar. */
  whenCookiesSettled(callback) {
    if (this.dataset.waitCookies !== 'true') return callback();

    let done = false;
    const go = () => { if (done) return; done = true; callback(); };

    const bannerVisible = () => {
      const el = document.getElementById('shopify-pc__banner');
      if (el && el.offsetParent !== null) return true;
      try {
        const privacy = window.Shopify && window.Shopify.customerPrivacy;
        return !!(privacy && typeof privacy.shouldShowBanner === 'function' && privacy.shouldShowBanner());
      } catch (e) { return false; }
    };

    // Señal oficial: el visitante aceptó o rechazó.
    document.addEventListener('visitorConsentCollected', go, { once: true });

    // Sondeo: espera a que el banner aparezca y desaparezca. Si en 2,5 s no ha
    // aparecido ninguno, se continúa. Tope de seguridad a los 20 s.
    let waited = 0;
    let sawBanner = false;
    const tick = () => {
      if (done) return;
      if (bannerVisible()) sawBanner = true;
      else if (sawBanner || waited >= 2500) return go();
      waited += 250;
      if (waited > 20000) return go();
      setTimeout(tick, 250);
    };
    tick();
  }
}
customElements.define('location-modal', LocationModal);

/* En el editor de temas: abrir el popup/modal al seleccionar su sección */
document.addEventListener('shopify:section:select', (e) => {
  e.target.querySelector('archive-gate overlay-el, location-modal overlay-el')?.open();
});
document.addEventListener('shopify:section:deselect', (e) => {
  e.target.querySelector('archive-gate overlay-el, location-modal overlay-el')?.close();
});

/* ── Facets (filtros con envío automático) ────────────────────── */
class FacetsForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('change', () => {
      const params = new URLSearchParams(new FormData(this.form));
      window.location.search = params.toString();
    });
  }
}
customElements.define('facets-form', FacetsForm);

/* ── Parallax sutil en fullbleed ──────────────────────────────── */
if (document.body.dataset.parallax === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  const parallaxEls = () => document.querySelectorAll('.fullbleed__media');
  let ticking = false;
  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      parallaxEls().forEach((el) => {
        const rect = el.parentElement.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        const progress = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
        el.style.transform = `translateY(${progress * -5}%)`;
      });
      ticking = false;
    });
  }, { passive: true });
}
